import React from "react";

import _ from "lodash";

export type UiExtenderType = {
    [key: string]: {
        [id: string]: {
            id: string;
            element: JSX.Element;
            priority: number;
        };
    };
};

export type UiElements = {
    key: string;
    id: string;
    element: JSX.Element;
    priority: number;
};

export const UiExtenderContext = React.createContext<UiExtenderType>({});

export const UiExtenderActionsContext = React.createContext({
    addElement: (key: string, id: string, element: JSX.Element, priority: number) => {
        // empty function
    },
    addElements: (elements: Array<UiElements>) => {
        // empty function
    },
    removeElement: (key: string, id: string) => {
        // empty function
    },
    removeElements: (elements: Array<{ key: string; id: string }>) => {
        // empty function
    },
});

export const useUiExtenderContext = () => React.useContext(UiExtenderContext);

export const useUiExtenderActionsContext = () => React.useContext(UiExtenderActionsContext);

type PropsType = {
    children: React.ReactNode;
};

export const UiExtenderContextProvider = ({ children }: PropsType) => {
    const [elements, setElements] = React.useState<UiExtenderType>({});

    const addElement = React.useCallback(
        (key: string, id: string, element: JSX.Element, priority: number) => {
            setElements((prev) => {
                const newElements = _.cloneDeep(prev);

                if (!(key in newElements)) {
                    newElements[key] = {};
                }

                newElements[key][id] = {
                    id,
                    element,
                    priority,
                };

                return newElements;
            });
        },
        []
    );

    const addElements = React.useCallback((elements: Array<UiElements>) => {
        setElements((prev) => {
            const newElements = _.cloneDeep(prev);

            elements.forEach((element) => {
                if (!(element.key in newElements)) {
                    newElements[element.key] = {};
                }

                newElements[element.key][element.id] = {
                    id: element.id,
                    element: element.element,
                    priority: element.priority,
                };
            });

            return newElements;
        });
    }, []);

    const removeElement = React.useCallback((key: string, id: string) => {
        setElements((prev) => {
            const newElements = _.cloneDeep(prev);

            if (key in newElements) {
                delete newElements[key][id];
            }

            return newElements;
        });
    }, []);

    const removeElements = React.useCallback((elements: Array<{ key: string; id: string }>) => {
        setElements((prev) => {
            const newElements = _.cloneDeep(prev);

            elements.forEach((element) => {
                if (element.key in newElements) {
                    delete newElements[element.key][element.id];
                }
            });

            return newElements;
        });
    }, []);

    const uiExtenderActionsValue = React.useMemo(
        () => ({ addElement, addElements, removeElement, removeElements }),
        [addElement, addElements, removeElement, removeElements]
    );

    return (
        <UiExtenderContext.Provider value={elements}>
            <UiExtenderActionsContext.Provider value={uiExtenderActionsValue}>
                {children}
            </UiExtenderActionsContext.Provider>
        </UiExtenderContext.Provider>
    );
};
